import { AlertTriangle, CheckCircle2, Circle, Clock, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';

export const EmergencyChecklistCard = ({ 
  checklist, 
  onActivate, 
  onDeactivate, 
  onToggleTask,
  criticalMode 
}) => {
  const completedCount = checklist.checklist.filter(t => t.completed).length;
  const totalCount = checklist.checklist.length;
  const completionPercentage = totalCount > 0 
    ? Math.round((completedCount / totalCount) * 100) 
    : 0;

  const formatActivatedTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <Card 
      className={`p-5 transition-smooth ${
        checklist.isActive 
          ? 'border-2 border-destructive bg-destructive/5 shadow-lg' 
          : 'hover:shadow-medium'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="text-3xl">{checklist.icon}</div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg mb-1">{checklist.type}</h3>
            <p className="text-xs text-muted-foreground">{checklist.description}</p>
          </div>
        </div>
        
        {checklist.isActive ? (
          <Badge variant="destructive" className="gap-1 shrink-0">
            <AlertTriangle className="h-3 w-3" />
            ACTIVE
          </Badge>
        ) : (
          <Badge variant="outline" className="shrink-0">Standby</Badge>
        )}
      </div>

      {/* Activation Time */}
      {checklist.isActive && checklist.activatedAt && (
        <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>Activated at {formatActivatedTime(checklist.activatedAt)}</span>
        </div>
      )}

      {/* Progress */}
      {checklist.isActive && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm text-muted-foreground">
              {completedCount}/{totalCount} tasks
            </span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
        </div>
      )}

      {/* Checklist Items */}
      {checklist.isActive && (
        <div className="space-y-2 mb-4">
          {checklist.checklist.map((task, index) => (
            <div 
              key={task.id}
              className={`flex items-start gap-3 p-3 rounded-lg transition-smooth ${
                task.completed 
                  ? 'bg-success/10 border border-success/20' 
                  : 'bg-muted/30 border border-border'
              }`}
            >
              <Checkbox
                id={task.id}
                checked={task.completed}
                onCheckedChange={() => onToggleTask(checklist.id, task.id)}
                className="mt-0.5"
              />
              <label
                htmlFor={task.id}
                className={`text-sm cursor-pointer flex-1 ${
                  task.completed 
                    ? 'line-through text-muted-foreground' 
                    : 'text-foreground'
                }`}
              >
                <span className="font-semibold mr-2">{index + 1}.</span>
                {task.task}
              </label>
              {task.completed ? (
                <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-3 border-t border-border">
        {checklist.isActive ? (
          <>
            <div className="flex-1 flex items-center gap-2 text-sm">
              {completionPercentage === 100 ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="text-success font-medium">All tasks completed!</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <span className="text-warning font-medium">
                    {totalCount - completedCount} tasks remaining
                  </span>
                </>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDeactivate(checklist.id)}
              className="gap-2"
            >
              <XCircle className="h-3.5 w-3.5" />
              Deactivate
            </Button>
          </>
        ) : (
          <>
            <div className="flex-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Circle className="h-4 w-4" />
              <span>{totalCount} emergency tasks ready</span>
            </div>
            {criticalMode && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onActivate(checklist.id)}
                className="gap-2"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Activate
              </Button>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default EmergencyChecklistCard;
